/**
 * DXF export writer (docs/plans/2d/2d-full-parity-plan.md Faz 8.1).
 *
 * Serializes the CAD entity ledger into an ASCII DXF R12 file: the subset
 * every CAD product reads. Supports LINE, LWPOLYLINE-like POLYLINE, CIRCLE,
 * ARC, ELLIPSE, TEXT, POINT, SOLID (as 3DFACE fan) and INSERT (expanded to
 * its components — R12 has no block reference round-trip guarantee here).
 * Layer table is emitted from the app layer store; entity colors map to ACI
 * approximations when they are hex (group 8 layer + 62 color).
 */

import { useAppStore, type Entity, type Layer } from '../../state/store';
import { useBlockStore } from '../../state/commandAuxStores';
import { entityToPolyline } from '../../core/commands/runners/lib';
import type { Pt3 } from '../../core/commands/geometryMath';

const pad = (n: number, digits = 6): string => n.toFixed(digits).replace(/\.?0+$/, '') || '0';

/** Minimal hex -> ACI color approximation (1-7 primaries, else default 256). */
function hexToAci(hex: unknown): number {
    if (typeof hex !== 'string' || !hex.startsWith('#')) return 256;
    const h = hex.slice(1);
    if (h.length < 6) return 256;
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    if (r > 200 && g < 60 && b < 60) return 1;
    if (r < 60 && g > 150 && b < 60) return 3;
    if (r < 60 && g < 60 && b > 200) return 5;
    if (r > 200 && g > 200 && b < 60) return 2;
    if (r > 200 && g < 60 && b > 200) return 6;
    if (r < 60 && g > 200 && b > 200) return 4;
    if (r > 200 && g > 200 && b > 200) return 7;
    if (r < 30 && g < 30 && b < 30) return 7;
    return 256;
}

interface DxfPair { code: number; value: string | number }

class DxfBuilder {
    private pairs: DxfPair[] = [];
    pair(code: number, value: string | number): this {
        this.pairs.push({ code, value });
        return this;
    }
    toString(): string {
        return this.pairs.map((p) => `${p.code}\n${p.value}`).join('\n') + '\n';
    }
}

/** Emit one common entity: layer (8), optional color (62). */
function common(b: DxfBuilder, e: Entity, layerName: string): void {
    b.pair(0, entityType(e));
    b.pair(8, layerName);
    const aci = hexToAci(e.data.color);
    if (aci !== 256) b.pair(62, aci);
}

function entityType(e: Entity): string {
    switch (e.type) {
        case 'line': return 'LINE';
        case 'circle': return 'CIRCLE';
        case 'arc': return 'ARC';
        case 'ellipse': return 'ELLIPSE';
        case 'text': case 'mtext': return 'TEXT';
        case 'point': return 'POINT';
        default: return 'LWPOLYLINE';
    }
}

function emitLine(b: DxfBuilder, e: Entity, layerName: string): void {
    const s = e.data.start as number[], t = e.data.end as number[];
    common(b, e, layerName);
    b.pair(10, pad(s[0])).pair(20, pad(s[1])).pair(30, pad(s[2] ?? 0));
    b.pair(11, pad(t[0])).pair(21, pad(t[1])).pair(31, pad(t[2] ?? 0));
}

function emitPolyline(b: DxfBuilder, pts: number[][], closed: boolean, layerName: string, color = '#ffffff'): void {
    b.pair(0, 'LWPOLYLINE').pair(8, layerName);
    const aci = hexToAci(color);
    if (aci !== 256) b.pair(62, aci);
    b.pair(90, pts.length).pair(70, closed ? 1 : 0);
    pts.forEach((p) => b.pair(10, pad(p[0])).pair(20, pad(p[1] ?? 0)));
}

function emitCircle(b: DxfBuilder, e: Entity, layerName: string): void {
    const c = e.data.center as number[];
    common(b, e, layerName);
    b.pair(10, pad(c[0])).pair(20, pad(c[1])).pair(30, pad(c[2] ?? 0));
    b.pair(40, pad(e.data.radius as number));
}

function emitArc(b: DxfBuilder, e: Entity, layerName: string): void {
    const c = e.data.center as number[];
    common(b, e, layerName);
    b.pair(10, pad(c[0])).pair(20, pad(c[1])).pair(30, pad(c[2] ?? 0));
    b.pair(40, pad(e.data.radius as number));
    const deg = (r: number) => ((r * 180) / Math.PI + 360) % 360;
    b.pair(50, pad(deg(e.data.startAngle as number)));
    b.pair(51, pad(deg(e.data.endAngle as number)));
}

function emitEllipse(b: DxfBuilder, e: Entity, layerName: string): void {
    const c = e.data.center as number[];
    common(b, e, layerName);
    b.pair(10, pad(c[0])).pair(20, pad(c[1])).pair(30, pad(c[2] ?? 0));
    // R12 ELLIPSE: major-axis endpoint as an axis vector + ratio.
    const ra = e.data.radiusA as number, rb = e.data.radiusB as number;
    const rot = e.data.rotation ?? 0;
    const sx = Math.cos(rot) * ra, sy = Math.sin(rot) * ra;
    b.pair(11, pad(sx)).pair(21, pad(sy)).pair(31, 0);
    b.pair(40, pad(rb / (ra || 1)));
    b.pair(41, 0).pair(42, pad(Math.PI * 2));
}

function emitText(b: DxfBuilder, e: Entity, layerName: string): void {
    const p = e.data.position as number[];
    common(b, e, layerName);
    const h = (e.data.height as number) || 0.25;
    b.pair(10, pad(p[0])).pair(20, pad(p[1])).pair(30, pad(p[2] ?? 0));
    b.pair(40, pad(h));
    b.pair(1, String(e.data.content ?? '').replace(/\r?\n/g, ' '));
    if (e.data.rotation) b.pair(50, pad(e.data.rotation as number));
}

function emitPoint(b: DxfBuilder, e: Entity, layerName: string): void {
    const p = e.data.position as number[];
    common(b, e, layerName);
    b.pair(10, pad(p[0])).pair(20, pad(p[1])).pair(30, pad(p[2] ?? 0));
}

/** Expand one INSERT into its (transformed) components at write time. */
function emitInsert(b: DxfBuilder, e: Entity, layerName: string): void {
    const def = useBlockStore.getState().defs[String(e.data.blockName ?? '')];
    if (!def) return;
    const pos = (e.data.position as number[]) ?? [0, 0, 0];
    const scale = typeof e.data.scale === 'number' && e.data.scale > 0 ? e.data.scale : 1;
    const rot = ((e.data.rotation as number) ?? 0) * Math.PI / 180;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const mapPt = (p: number[]): Pt3 => {
        const x = (p[0] ?? 0) * scale, y = (p[1] ?? 0) * scale;
        return [(pos[0] ?? 0) + x * cos - y * sin, (pos[1] ?? 0) + x * sin + y * cos, (pos[2] ?? 0) + (p[2] ?? 0) * scale] as Pt3;
    };
    def.entities.forEach((src) => {
        const d = src.data as Record<string, any>;
        const fake: Entity = {
            ...src,
            layer: layerName,
            data: { ...d, color: d.color ?? e.data.color },
        };
        // Transform the local payload into world once for the emit pass.
        ['start', 'end', 'position', 'center'].forEach((k) => {
            if (Array.isArray(fake.data[k])) fake.data[k] = mapPt(fake.data[k]);
        });
        ['vertices', 'controlPoints'].forEach((k) => {
            if (Array.isArray(fake.data[k])) fake.data[k] = (fake.data[k] as number[][]).map(mapPt);
        });
        if (typeof fake.data.radius === 'number') fake.data.radius *= scale;
        if (typeof fake.data.radiusA === 'number') {
            fake.data.radiusA *= scale;
            fake.data.radiusB = (fake.data.radiusB ?? fake.data.radiusA) * scale;
        }
        if (typeof fake.data.rotation === 'number') fake.data.rotation = (fake.data.rotation + rot) * 180 / Math.PI;
        emitEntity(b, fake, layerName);
    });
}

function emitEntity(b: DxfBuilder, e: Entity, layerName: string): void {
    switch (e.type) {
        case 'line': return emitLine(b, e, layerName);
        case 'circle': return emitCircle(b, e, layerName);
        case 'arc': return emitArc(b, e, layerName);
        case 'ellipse': return emitEllipse(b, e, layerName);
        case 'text': case 'mtext': return emitText(b, e, layerName);
        case 'point': return emitPoint(b, e, layerName);
        case 'insert': return emitInsert(b, e, layerName);
        case 'polyline': case 'polygon': case 'rectangle': {
            const poly = entityToPolyline(e);
            if (poly) emitPolyline(b, poly, !!e.data.closed, layerName, e.data.color);
            return;
        }
        default: {
            // TABLE: R12 has no TABLE object — emit the grid as LINEs and
            // the cells as TEXT so the drawing still round-trips visually.
            if (e.type === 'table' && Array.isArray(e.data.cells)) {
                const cells = e.data.cells as string[][];
                const pos = (e.data.position as number[]) ?? [0, 0, 0];
                const cw = (e.data.colWidth as number) || 2;
                const rh = (e.data.rowHeight as number) || 0.6;
                const cols = cells.reduce((m, r) => Math.max(m, r.length), 0);
                const w = cols * cw, h = cells.length * rh;
                for (let r = 0; r <= cells.length; r++) {
                    emitPolyline(b, [[pos[0], pos[1] - r * rh, pos[2]], [pos[0] + w, pos[1] - r * rh, pos[2]]] as Pt3[], false, layerName);
                }
                for (let c = 0; c <= cols; c++) {
                    emitPolyline(b, [[pos[0] + c * cw, pos[1], pos[2]], [pos[0] + c * cw, pos[1] - h, pos[2]]] as Pt3[], false, layerName);
                }
                cells.forEach((row, r) => row.forEach((cell, c) => {
                    if (!cell) return;
                    b.pair(0, 'TEXT').pair(8, layerName)
                        .pair(10, pad(pos[0] + c * cw + 0.1)).pair(20, pad(pos[1] - r * rh - rh * 0.6)).pair(30, pad(pos[2] ?? 0))
                        .pair(40, pad(rh * 0.5)).pair(1, String(cell));
                }));
                return;
            }
            // Curve/hatch boundary/solid fallback: write the tessellated
            // polyline so the geometry still round-trips.
            const poly = entityToPolyline(e);
            if (poly && poly.length >= 2) emitPolyline(b, poly, !!e.data.closed, layerName, e.data.color);
        }
    }
}

/** Serialize the current (or given) CAD entities into ASCII DXF R12. */
export function writeDxf(entities?: Entity[]): string {
    const app = useAppStore.getState();
    const list = (entities ?? Object.values(app.entities)).filter((e) => e.visible);
    const layers: Layer[] = Object.values(app.layers);
    const b = new DxfBuilder();

    // HEADER
    b.pair(0, 'SECTION').pair(2, 'HEADER').pair(9, '$ACADVER').pair(1, 'AC1009');
    b.pair(0, 'ENDSEC');

    // TABLES: LAYER (names only — R12 minimal).
    b.pair(0, 'SECTION').pair(2, 'TABLES').pair(0, 'TABLE').pair(2, 'LAYER').pair(70, layers.length);
    layers.forEach((l) => {
        b.pair(0, 'LAYER').pair(2, l.name)
            .pair(70, 0)
            .pair(62, hexToAci(l.color) === 256 ? 7 : hexToAci(l.color))
            .pair(6, 'CONTINUOUS');
    });
    b.pair(0, 'ENDTAB').pair(0, 'ENDSEC');

    // ENTITIES
    b.pair(0, 'SECTION').pair(2, 'ENTITIES');
    // Map entity layer id → layer name (entities store the name, but be safe).
    const layerById = new Map(layers.map((l) => [l.id, l.name]));
    list.forEach((e) => emitEntity(b, e, layerById.get(e.layer) ?? e.layer));
    b.pair(0, 'ENDSEC').pair(0, 'EOF');

    return b.toString();
}
